import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { currentProfileAtom } from "../../../features/Club/atoms";
import {
  WhooshMutationError,
  useMutationWithError,
} from "../../../utils/hooks/useMutationWithError";
import {
  UpdateEventSignUpInput,
  useUpdateEventSignUpMutation,
  useUpdateEventSignUpMutation$data,
} from "./__generated__/useUpdateEventSignUpMutation.graphql";

export const useUpdateEventSignUp = (
  successCallback: () => void,
  errorCallback?: () => void
) => {
  const { showToast } = useToast();

  const [updateEventSignUp, isAdding] =
    useMutationWithError<useUpdateEventSignUpMutation>(
      useUpdateEventSignupMutationString
    );

  const currentProfile = useAtomValue(currentProfileAtom);
  const memberId = currentProfile?.memberId;

  const onCompleted = useCallback(
    (res: useUpdateEventSignUpMutation$data) => {
      if (res.responsePayload?.success) {
        showToast({
          description: t`Registration Successfully Updated.`,
          semanticStyle: "success",
          toastId: "update-registration-success",
        });
        successCallback();
      }
    },
    [showToast, successCallback]
  );

  const onCompletedErrorCustom = useCallback(
    (errors: WhooshMutationError[]) => {
      errorCallback?.();

      if (errors.find((e) => e.code === "already_taken")) {
        showToast({
          description: t`One of your group members is already signed up for the event. Please remove them from your group and try again.`,
          toastId: "update-registration-error-already-taken",
          semanticStyle: "error",
        });
      } else {
        showToast({
          description: t`Sorry. An error occurred while creating your registration.`,
          toastId: "update-registration-error",
          semanticStyle: "error",
        });
      }

      return [];
    },
    [errorCallback, showToast]
  );
  const onUpdateError = useCallback(() => {
    errorCallback?.();
  }, [errorCallback]);

  const handleUpdateEventSignUp = useCallback(
    (input: UpdateEventSignUpInput) => {
      if (memberId) {
        updateEventSignUp({
          variables: { input, memberId },
          onCompleted,
          onCompletedErrorCustom,
          onErrorCustom: onUpdateError,
          onPayloadError: onUpdateError,
        });
      } else {
        onUpdateError();
      }
    },
    [
      memberId,
      onCompleted,
      onCompletedErrorCustom,
      onUpdateError,
      updateEventSignUp,
    ]
  );

  return [handleUpdateEventSignUp, isAdding] as const;
};

const useUpdateEventSignupMutationString = graphql`
  mutation useUpdateEventSignUpMutation(
    $input: UpdateEventSignUpInput!
    $memberId: ID!
  ) {
    responsePayload: updateEventSignUp(input: $input) {
      errors {
        field
        code
        message
      }
      success
      eventSignUp {
        waitlistIndex
        id
        checkedIn
        paymentCardOnFile {
          id
        }
        ...EventSignupSummaryContent_signUp
        event {
          eventSeries {
            events {
              edges {
                node {
                  accessStatusForClient(clientId: $memberId)
                  signUpStatusForClient(clientId: $memberId)
                }
              }
            }
          }
          ...EventDetailAttendeesList
          ...EventDetailGroupDisplay_event
          ...useCurrentSignUp_event
          ...useInitEventSignUps_event
          ...NewEventDetailAttendeesList
        }
      }
    }
  }
`;
